/**
 * ComboboxBusca — input com busca sobre lista pré-carregada, STRICT:
 * só vale o que foi selecionado da lista (digitar limpa a seleção).
 *
 * Props:
 * - `opcoes`: [{ id, label, sublabel?, ...extras }]
 * - `valor`: opção selecionada ou null
 * - `onSelecionar(opcao|null)`
 * - `onTextoChange?(texto)` — para o pai validar "digitou mas não escolheu"
 * - `placeholder`, `carregando`, `inputId`
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";

import { filtrarTolerante } from "../../../utils/buscaTolerante";
import {
  ComboDropdown, ComboItem, ComboLimparBtn, ComboVazio, ComboWrap, Input,
} from "./styles";

const MAX_RESULTADOS = 30;

// Busca tolerante a acento, ordem das palavras e erro de digita\u00e7\u00e3o \u2014 o
// montador digita no celular e "carvalio"/"grafitte" precisam achar.
// `codigo` entra na busca quando a op\u00e7\u00e3o tem (placeholder promete isso).
const camposBuscaveis = (o) => [o.label, o.sublabel, o.codigo];

export default function ComboboxBusca({
  opcoes,
  valor,
  onSelecionar,
  onTextoChange,
  placeholder,
  carregando = false,
  inputId,
}) {
  const [texto, setTexto] = useState(valor?.label || "");
  const [aberto, setAberto] = useState(false);
  const blurTimer = useRef(null);

  // Seleção feita fora (ex.: rascunho recuperado) reflete no input.
  useEffect(() => {
    setTexto(valor?.label || "");
  }, [valor]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const filtradas = useMemo(() => {
    const base = texto.trim() && !valor
      ? filtrarTolerante(opcoes, texto, camposBuscaveis)
      : opcoes;
    return base.slice(0, MAX_RESULTADOS);
  }, [opcoes, texto, valor]);

  const escolher = (opcao) => {
    onSelecionar(opcao);
    onTextoChange?.(opcao.label);
    setAberto(false);
  };

  const limpar = () => {
    onSelecionar(null);
    onTextoChange?.("");
    setTexto("");
  };

  return (
    <ComboWrap>
      <Input
        id={inputId}
        value={texto}
        placeholder={carregando ? "Carregando…" : placeholder}
        disabled={carregando}
        autoComplete="off"
        onChange={(e) => {
          setTexto(e.target.value);
          onTextoChange?.(e.target.value);
          if (valor) onSelecionar(null);
          setAberto(true);
        }}
        onFocus={() => setAberto(true)}
        onBlur={() => {
          clearTimeout(blurTimer.current);
          blurTimer.current = setTimeout(() => setAberto(false), 180);
        }}
      />
      {valor && (
        <ComboLimparBtn type="button" aria-label="Limpar seleção" onClick={limpar}>
          <FiX size={18} />
        </ComboLimparBtn>
      )}
      {aberto && !carregando && (
        <ComboDropdown>
          {filtradas.length === 0 ? (
            <ComboVazio>Nenhum resultado. Só é possível escolher da lista.</ComboVazio>
          ) : (
            filtradas.map((opcao) => (
              <ComboItem
                key={opcao.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => escolher(opcao)}
              >
                {opcao.label}
                {opcao.sublabel ? <small>{opcao.sublabel}</small> : null}
              </ComboItem>
            ))
          )}
        </ComboDropdown>
      )}
    </ComboWrap>
  );
}
